import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { iconFor } from "../lib/categories";
import EnquiryForm from "../components/EnquiryForm";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Globe, Mail, MapPin, Phone, ArrowLeft, Loader2, Tag } from "lucide-react";
import Countdown from "../components/Countdown";

export default function ListingPage() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => {
      setL(r.data);
      if (r.data.status === "approved") api.post(`/listings/${id}/events`, { type: "view" }).catch(() => {});
    }).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="listing-not-found">
        <p className="font-display text-3xl font-bold">Listing not found</p>
        <Link to="/search" className="underline mt-4 inline-block">Back to the directory</Link>
      </div>
    );
  }
  if (!l) return <div className="py-24 flex justify-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const Icon = iconFor(l.category);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="listing-page">
      <Link to={`/search?category=${encodeURIComponent(l.category)}`} className="text-sm text-zinc-600 hover:text-black inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-4 h-4" /> More in {l.category}
      </Link>
      {l.status !== "approved" && (
        <div className="mb-6 p-3 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="listing-preview-banner">
          This listing has been <strong>taken down</strong> and is not visible to the public.
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500">
            <Icon className="w-3.5 h-3.5" /> {l.category}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-2" data-testid="listing-name">{l.business_name}</h1>
          {l.tagline && <p className="text-lg text-zinc-600 mt-2">{l.tagline}</p>}
          <p className="text-sm text-zinc-500 mt-3 flex items-center gap-1"><MapPin className="w-4 h-4" /> {l.town} · {l.postcode}{l.areas_covered && ` · Covers ${l.areas_covered}`}</p>

          {l.active_offer && (
            <div className="mt-6 p-5 rounded-lg bg-[#FFCC00] border border-black flex gap-4 items-start" data-testid="listing-offer">
              <Tag className="w-5 h-5 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-widest">Today&apos;s offer</span>
                  {l.active_offer.label && <span className="px-2 py-0.5 rounded bg-black text-[#FFCC00] text-xs font-bold">{l.active_offer.label}</span>}
                  <Countdown to={l.active_offer.expires_at} className="text-[10px] font-mono uppercase tracking-widest ml-auto" />
                </div>
                <p className="font-display text-xl font-bold mt-1">{l.active_offer.title}</p>
                <p className="text-sm mt-1 whitespace-pre-wrap">{l.active_offer.description}</p>
              </div>
            </div>
          )}

          {l.photos?.length > 0 && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="listing-gallery">
              {l.photos.map((p, i) => (
                <button key={p} onClick={() => setZoom(p)} data-testid={`gallery-photo-${i}`}
                  className={`overflow-hidden rounded-md border border-zinc-200 ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}>
                  <img src={mediaUrl(p)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </button>
              ))}
            </div>
          )}

          <h2 className="font-display text-2xl font-bold mt-10 mb-3">About</h2>
          <p className="text-zinc-700 whitespace-pre-wrap leading-relaxed" data-testid="listing-description">{l.description}</p>

          {l.services?.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-bold mt-10 mb-3">Services</h2>
              <div className="flex flex-wrap gap-2" data-testid="listing-services">
                {l.services.map((s) => <span key={s} className="px-3 py-1.5 border border-zinc-200 rounded-md text-sm">{s}</span>)}
              </div>
            </>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 self-start">
          <ContactCard l={l} />
          {l.status === "approved" && <EnquiryForm listingId={l.id} businessName={l.business_name} />}
        </aside>
      </div>

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent className="max-w-4xl p-2" data-testid="gallery-lightbox">
          {zoom && <img src={mediaUrl(zoom)} alt="" className="w-full max-h-[80vh] object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactCard({ l }) {
  const rows = [
    l.phone && { Icon: Phone, label: l.phone, href: `tel:${l.phone.replace(/\s/g, "")}`, id: "phone" },
    { Icon: Mail, label: l.email, href: `mailto:${l.email}`, id: "email" },
    l.website && { Icon: Globe, label: l.website.replace(/^https?:\/\//, ""), href: l.website, id: "website" },
  ].filter(Boolean);
  return (
    <div className="p-6 bg-black text-white rounded-lg" data-testid="contact-card">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-300 mb-4">Contact directly</p>
      <div className="space-y-3">
        {rows.map(({ Icon, label, href, id }) => (
          <a key={id} href={href} target={id === "website" ? "_blank" : undefined} rel="noopener noreferrer"
            data-testid={`contact-${id}`}
            onClick={() => l.status === "approved" && api.post(`/listings/${l.id}/events`, { type: `contact_${id}` }).catch(() => {})}
            className="flex items-center gap-3 p-3 rounded-md bg-zinc-900 hover:bg-zinc-800 transition-colors break-all">
            <Icon className="w-4 h-4 text-[#FFCC00] shrink-0" /> <span className="text-sm">{label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
